import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterModule, Router } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import {
  AdminService,
  AdminActionHistory,
  NotLatestStatusError,
  LatestActionInfo,
} from '../../services/admin.service';

@Component({
  selector: 'app-admin-actions',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterModule],
  templateUrl: './admin-actions.component.html',
  styleUrl: './admin-actions.component.css',
})
export class AdminActionsComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  actions: AdminActionHistory[] = [];
  loading = true;
  error: string | null = null;
  processing = false;

  currentPage = 1;
  totalPages = 1;
  totalItems = 0;
  itemsPerPage = 20;

  showEditModal = false;
  selectedAction: AdminActionHistory | null = null;
  newActionType: 'approve' | 'decline' = 'approve';
  editReason = '';

  showLatestStatusWarning = false;
  latestActionInfo: LatestActionInfo | null = null;

  messages: {
    success: string | null;
    error: string | null;
    warning: string | null;
  } = {
    success: null,
    error: null,
    warning: null,
  };

  constructor(private adminService: AdminService, private router: Router) {}

  ngOnInit(): void {
    this.loadActionHistory();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadActionHistory(): void {
    this.loading = true;
    this.error = null;

    this.adminService
      .getAdminActionHistory(this.currentPage, this.itemsPerPage)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.actions = response.actions || [];
          this.currentPage = response.pagination.page || 1;
          this.totalPages = response.pagination.pages || 1;
          this.totalItems = response.pagination.total || 0;
          this.loading = false;
        },
        error: (error) => {
          console.error('Error loading action history:', error);
          this.error = error.message || 'Failed to load action history';
          this.actions = [];
          this.totalPages = 1;
          this.totalItems = 0;
          this.currentPage = 1;
          this.loading = false;
        },
      });
  }

  onRefresh(): void {
    this.dismissAllMessages();
    this.currentPage = 1;
    this.loadActionHistory();
  }

  onPageChange(page: number): void {
    if (page >= 1 && page <= this.totalPages && page !== this.currentPage) {
      this.currentPage = page;
      this.loadActionHistory();
    }
  }

  goBack(): void {
    this.router.navigate(['/admin/dashboard']);
  }

  canEdit(action: AdminActionHistory): boolean {
    return this.adminService.canEditAction(action);
  }

  openEditModal(action: AdminActionHistory): void {
    this.selectedAction = action;
    this.newActionType =
      action.action_type === 'approve_listing' ? 'decline' : 'approve';
    this.editReason = '';
    this.showEditModal = true;
    this.showLatestStatusWarning = false;
    this.latestActionInfo = null;
    this.dismissAllMessages();
  }

  closeEditModal(): void {
    this.showEditModal = false;
    this.selectedAction = null;
    this.editReason = '';
    this.showLatestStatusWarning = false;
    this.latestActionInfo = null;
  }

  confirmEdit(): void {
    if (!this.selectedAction || this.processing) return;

    if (this.newActionType === 'decline' && !this.editReason.trim()) {
      this.showErrorMessage('Please provide a reason for declining');
      return;
    }

    this.processing = true;

    this.adminService
      .updateAdminAction(
        this.selectedAction.id,
        this.newActionType,
        this.editReason.trim() || undefined
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.showSuccessMessage(
            `Action updated to ${this.newActionType} successfully. New action record created.`
          );
          this.closeEditModal();
          this.processing = false;

          setTimeout(() => {
            this.loadActionHistory();
          }, 500);
        },
        error: (error) => {
          console.error('Error updating action:', error);

          if (this.isNotLatestStatusError(error)) {
            this.handleNotLatestStatusError(error);
          } else {
            this.showErrorMessage(error.message || 'Failed to update action');
          }

          this.processing = false;
        },
      });
  }

  private isNotLatestStatusError(error: any): error is NotLatestStatusError {
    return error && error.errorType === 'not_latest_status';
  }

  private handleNotLatestStatusError(error: NotLatestStatusError): void {
    this.showLatestStatusWarning = true;
    this.latestActionInfo = error.latestAction;

    this.showWarningMessage(error.message);

    setTimeout(() => {
      this.loadActionHistory();
    }, 1000);
  }

  highlightLatestAction(actionId: string): boolean {
    return this.latestActionInfo?.id === actionId;
  }

  formatActionType(actionType: string): string {
    return this.adminService.formatActionType(actionType);
  }

  formatDate(dateString: string): string {
    try {
      return new Date(dateString).toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch (error) {
      return 'Invalid date';
    }
  }

  getActionIcon(actionType: string): string {
    switch (actionType) {
      case 'approve_listing':
        return '✓';
      case 'decline_listing':
        return '✗';
      case 'bulk_approve_all':
        return 'BULK';
      default:
        return 'ACTION';
    }
  }

  getActionClass(actionType: string): string {
    switch (actionType) {
      case 'approve_listing':
        return 'action-approve';
      case 'decline_listing':
        return 'action-decline';
      case 'bulk_approve_all':
        return 'action-bulk';
      default:
        return 'action-default';
    }
  }

  getListingTitle(action: AdminActionHistory): string {
    try {
      const details = action.details;
      return details?.listing_title || `Listing #${action.target_id}`;
    } catch (error) {
      return `Listing #${action.target_id}`;
    }
  }

  getActionReason(action: AdminActionHistory): string {
    try {
      const details = action.details;
      if (details?.modification_type === 'admin_action_update') {
        const originalType =
          details.original_action_type === 'approve_listing'
            ? 'Approve'
            : 'Decline';
        const newType =
          action.action_type === 'approve_listing' ? 'Approve' : 'Decline';
        const reason = details?.reason || 'No specific reason provided';
        return `Updated from ${originalType} to ${newType}. Reason: ${reason}`;
      }
      return details?.reason || details?.note || 'No reason provided';
    } catch (error) {
      return 'No reason provided';
    }
  }

  isModifiedAction(action: AdminActionHistory): boolean {
    try {
      return action.details?.modification_type === 'admin_action_update';
    } catch (error) {
      return false;
    }
  }

  getOriginalActionInfo(action: AdminActionHistory): string | null {
    try {
      if (this.isModifiedAction(action)) {
        const originalType =
          action.details.original_action_type === 'approve_listing'
            ? 'Approved'
            : 'Declined';
        const originalDate = new Date(
          action.details.original_action_date
        ).toLocaleString('en-US', {
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        });
        return `Originally ${originalType} on ${originalDate}`;
      }
      return null;
    } catch (error) {
      return null;
    }
  }

  getRemainingEditDays(action: AdminActionHistory): number {
    const actionDate = new Date(action.created_at);
    const now = new Date();
    const daysDiff = Math.floor(
      (now.getTime() - actionDate.getTime()) / (1000 * 60 * 60 * 24)
    );
    return Math.max(0, 7 - daysDiff);
  }

  trackByActionId(index: number, action: AdminActionHistory): string {
    return action.id;
  }

  getPageNumbers(): number[] {
    const pages = [];
    const maxVisible = 5;
    let start = Math.max(1, this.currentPage - Math.floor(maxVisible / 2));
    let end = Math.min(this.totalPages, start + maxVisible - 1);

    if (end - start < maxVisible - 1) {
      start = Math.max(1, end - maxVisible + 1);
    }

    for (let i = start; i <= end; i++) {
      pages.push(i);
    }

    return pages;
  }

  shouldShowEllipsis(position: 'start' | 'end'): boolean {
    const maxVisible = 5;
    const halfVisible = Math.floor(maxVisible / 2);

    if (position === 'start') {
      return this.currentPage > halfVisible + 1 && this.totalPages > maxVisible;
    } else {
      return this.currentPage < this.totalPages - halfVisible && this.totalPages > maxVisible;
    }
  }

  private showSuccessMessage(message: string): void {
    this.messages.success = message;
    this.messages.error = null;
    this.messages.warning = null;
    setTimeout(() => (this.messages.success = null), 5000);
  }

  private showErrorMessage(message: string): void {
    this.messages.error = message;
    this.messages.success = null;
    this.messages.warning = null;
    setTimeout(() => (this.messages.error = null), 8000);
  }

  private showWarningMessage(message: string): void {
    this.messages.warning = message;
    this.messages.error = null;
    this.messages.success = null;
    setTimeout(() => (this.messages.warning = null), 10000);
  }

  dismissMessage(type: 'success' | 'error' | 'warning'): void {
    this.messages[type] = null;
  }

  private dismissAllMessages(): void {
    this.messages.success = null;
    this.messages.error = null;
    this.messages.warning = null;
  }
}
